/**
 * Product Configurator Hook
 *
 * Manages state and logic for product configuration
 */

import { useState, useMemo } from 'react';
import type {
  ProductConfiguration,
  ProductVariant,
  AddOn,
  ConfiguratorState,
  ConfiguratorActions,
} from './types';

export function useConfigurator(
  initialProduct: ProductConfiguration['baseProduct']
): ConfiguratorState & ConfiguratorActions {
  const [configuration, setConfiguration] = useState<ProductConfiguration>({
    baseProduct: initialProduct,
    selectedVariant: null,
    selectedAddOns: [],
  });

  // Calculate total price
  const totalPrice = useMemo(() => {
    let total = configuration.selectedVariant?.price || 0;

    configuration.selectedAddOns.forEach((addon) => {
      total += addon.price;
    });

    return total;
  }, [configuration.selectedVariant, configuration.selectedAddOns]);

  // Validate configuration
  const { isValid, errors } = useMemo(() => {
    const validationErrors: string[] = [];

    if (!configuration.selectedVariant) {
      validationErrors.push('Please select a color');
    }

    if (configuration.selectedVariant && configuration.selectedVariant.stock === 0) {
      validationErrors.push('Selected color is out of stock');
    }

    // Check add-on stock
    configuration.selectedAddOns.forEach((addon) => {
      if (addon.stock === 0) {
        validationErrors.push(`${addon.name} is out of stock`);
      }
    });

    return {
      isValid: validationErrors.length === 0,
      errors: validationErrors,
    };
  }, [configuration]);

  // Actions
  const selectVariant = (variant: ProductVariant) => {
    setConfiguration((prev) => ({
      ...prev,
      selectedVariant: variant,
    }));
  };

  const toggleAddOn = (addon: AddOn) => {
    setConfiguration((prev) => {
      const isSelected = prev.selectedAddOns.some((a) => a.id === addon.id);

      if (isSelected) {
        // Remove addon
        return {
          ...prev,
          selectedAddOns: prev.selectedAddOns.filter((a) => a.id !== addon.id),
        };
      } else {
        // Add addon
        return {
          ...prev,
          selectedAddOns: [...prev.selectedAddOns, addon],
        };
      }
    });
  };

  const isAddOnSelected = (addonId: string): boolean => {
    return configuration.selectedAddOns.some((a) => a.id === addonId);
  };

  const clearConfiguration = () => {
    setConfiguration({
      baseProduct: initialProduct,
      selectedVariant: null,
      selectedAddOns: [],
    });
  };

  return {
    configuration,
    totalPrice,
    isValid,
    errors,
    selectVariant,
    toggleAddOn,
    isAddOnSelected,
    clearConfiguration,
  };
}
